<template>
  <article :class="['flex flex-col gap-5 rounded-xl border bg-card p-5 shadow-sm transition-colors', holiday.confirmed ? 'border-emerald-200 dark:border-emerald-900' : 'border-border']">
    <header class="flex items-start justify-between gap-4">
      <div class="min-w-0">
        <p v-if="contextLabel" class="mb-1 truncate text-xs font-semibold uppercase tracking-wide text-cyan-700 dark:text-cyan-300">
          {{ contextLabel }}
        </p>
        <h2 class="truncate text-lg font-semibold">
          {{ holiday.name }}
        </h2>
        <p class="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
          <CalendarDays class="h-4 w-4" />
          {{ formattedDate }}
        </p>
      </div>
      <span v-if="holiday.locked" class="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        <LockKeyhole class="h-3.5 w-3.5" /> Locked
      </span>
      <span v-else-if="holiday.confirmed" class="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
        <CircleCheck class="h-3.5 w-3.5" /> Confirmed
      </span>
      <span v-else class="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">Needs confirmation</span>
    </header>

    <template v-if="holiday.locked && !allowLockedEditing">
      <dl class="grid gap-3 rounded-lg bg-muted/40 p-4 text-sm sm:grid-cols-2">
        <div>
          <dt class="text-muted-foreground">
            Schedule
          </dt>
          <dd class="mt-1 font-medium">
            {{ holiday.open ? 'Open' : 'Closed' }}
          </dd>
        </div>
        <div v-if="holiday.open">
          <dt class="text-muted-foreground">
            Closing time
          </dt>
          <dd class="mt-1 font-medium">
            {{ formattedTime }}
          </dd>
        </div>
        <div v-if="holiday.notes" class="sm:col-span-2">
          <dt class="text-muted-foreground">
            Notes
          </dt>
          <dd class="mt-1 whitespace-pre-wrap">
            {{ holiday.notes }}
          </dd>
        </div>
      </dl>
    </template>

    <form v-else class="flex flex-1 flex-col gap-4" @submit.prevent="submit">
      <fieldset class="flex flex-col gap-2" :disabled="saving">
        <legend class="mb-2 text-sm font-medium">
          Will your business be open?
        </legend>
        <div class="grid grid-cols-2 rounded-lg bg-muted p-1">
          <button type="button" :aria-pressed="isOpen" :class="scheduleButtonClass(isOpen)" @click="selectOpen">
            Open
          </button>
          <button type="button" :aria-pressed="!isOpen" :class="scheduleButtonClass(!isOpen)" @click="isOpen = false">
            Closed
          </button>
        </div>
      </fieldset>

      <label v-if="isOpen" class="flex flex-col gap-2 text-sm font-medium">
        Closing time
        <input v-model="closingAt" type="time" class="h-10 rounded-md border border-input bg-background px-3 text-sm" required :disabled="saving">
      </label>

      <label class="flex flex-1 flex-col gap-2 text-sm font-medium">
        Notes <span class="font-normal text-muted-foreground">(optional)</span>
        <textarea v-model="notes" rows="3" maxlength="5000" class="min-h-20 resize-y rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Add special instructions or details" :disabled="saving" />
      </label>

      <Button type="submit" class="mt-auto w-full gap-2" :loading="saving">
        <Save class="h-4 w-4" />
        {{ holiday.confirmed ? 'Save changes' : 'Confirm schedule' }}
      </Button>
    </form>
  </article>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { CalendarDays, CircleCheck, LockKeyhole, Save } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'

const props = defineProps({
  holiday: { type: Object, required: true },
  saving: { type: Boolean, default: false },
  allowLockedEditing: { type: Boolean, default: false },
  contextLabel: { type: String, default: '' },
  recordId: { type: [Number, String], default: null },
})

const emit = defineEmits(['save'])
const isOpen = ref(false)
const closingAt = ref('17:00')
const notes = ref('')

const resetForm = () => {
  isOpen.value = props.holiday.open === true
  closingAt.value = props.holiday.closing_at?.slice(0, 5) ?? '17:00'
  notes.value = props.holiday.notes ?? ''
}

const selectOpen = () => {
  isOpen.value = true
  closingAt.value ||= '17:00'
}

const formattedDate = computed(() => {
  const date = new Date(`${props.holiday.date}T00:00:00`)

  return Number.isNaN(date.getTime()) ? props.holiday.date : new Intl.DateTimeFormat('en-US', { dateStyle: 'long' }).format(date)
})

const formattedTime = computed(() => {
  const [hours, minutes] = (props.holiday.closing_at ?? '17:00').split(':')

  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(2000, 0, 1, Number(hours), Number(minutes)))
})

const scheduleButtonClass = active => [
  'rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50',
  active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
]

const submit = () => {
  emit('save', {
    recordId: props.recordId,
    ciaHolidayId: props.holiday.holiday_id,
    schedule: {
      open: isOpen.value,
      closing_at: isOpen.value ? closingAt.value : null,
      notes: notes.value || null,
    },
  })
}

watch(() => props.holiday, resetForm, { immediate: true, deep: true })
</script>
